import { useState } from "react";
import { Lock, Unlock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { intelApi, setOwnerPin } from "../../lib/intelApi";

export function OwnerBadge({ unlocked, onClick }) {
  return (
    <button
      type="button"
      data-testid="owner-unlock-button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
        unlocked ? "border-accent text-accent" : "border-border text-muted-foreground hover:border-accent hover:text-accent"
      }`}
    >
      {unlocked ? <Unlock size={12} /> : <Lock size={12} />}
      {unlocked ? "Owner mode" : "Owner"}
    </button>
  );
}

export function OwnerPinDialog({ open, onOpenChange, onUnlocked, unlocked }) {
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;
    setBusy(true);
    try {
      await intelApi.verifyPin(pin.trim());
      setOwnerPin(pin.trim());
      onUnlocked(true);
      toast.success("Owner mode unlocked. Save & read actions enabled.");
      setPin("");
      onOpenChange(false);
    } catch (err) {
      toast.error("Incorrect PIN.");
    } finally {
      setBusy(false);
    }
  };

  const lock = () => {
    setOwnerPin("");
    onUnlocked(false);
    onOpenChange(false);
    toast("Owner mode locked.");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-none border-border bg-background" data-testid="owner-pin-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-black uppercase tracking-tight text-2xl">
            Owner <span className="text-accent">access</span>
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Save and mark-read are personal actions. Enter the owner PIN once — it stays in this browser only.
          </DialogDescription>
        </DialogHeader>
        {unlocked ? (
          <button
            type="button"
            data-testid="owner-lock-button"
            onClick={lock}
            className="w-full border border-border px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:border-accent hover:text-accent transition-colors"
          >
            Lock owner mode
          </button>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <input
              data-testid="owner-pin-input"
              type="password"
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Owner PIN"
              className="w-full bg-transparent border-b border-border focus:border-accent focus:outline-none py-3 font-mono text-base transition-colors"
            />
            <button
              type="submit"
              disabled={busy}
              data-testid="owner-pin-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-foreground hover:text-background transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Unlock size={13} />} Unlock
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
